import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { PlanInfo, UsageInfo } from "@/hooks/useDashboardData";
import type { DailySnapshot } from "@/mocks/dashboardCharts";

interface StatsCardsProps {
  plan: PlanInfo;
  usage: UsageInfo;
  history: DailySnapshot[];
}

function useTrend(history: DailySnapshot[], dataKey: keyof DailySnapshot) {
  return useMemo(() => {
    const recent = history.slice(-7);
    if (recent.length < 2) return { pct: 0, direction: "flat" as const };
    const first = recent[0][dataKey] as number;
    const last = recent[recent.length - 1][dataKey] as number;
    if (first === 0) {
      return { pct: last > 0 ? 100 : 0, direction: last > first ? "up" as const : "flat" as const };
    }
    const pct = Math.round(((last - first) / first) * 100);
    if (pct > 2) return { pct, direction: "up" as const };
    if (pct < -2) return { pct, direction: "down" as const };
    return { pct, direction: "flat" as const };
  }, [history, dataKey]);
}

function TrendBadge({ pct, direction, barColor }: { pct: number; direction: "up" | "down" | "flat"; barColor: string }) {
  const icon = direction === "up" ? "ri-arrow-up-line" : direction === "down" ? "ri-arrow-down-line" : "ri-arrow-right-line";
  const isPositive = direction === "up";
  const isNegative = direction === "down";

  return (
    <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${
      isPositive ? `${barColor.replace("bg-", "bg-").replace("500", "100")} ${barColor.replace("bg-", "text-").replace("500", "700")}` :
      isNegative ? "bg-red-100 text-red-600" :
      "bg-background-100 text-foreground-500"
    }`}>
      <i className={`${icon} text-xs`}></i>
      {isPositive ? "+" : isNegative ? "" : ""}{pct}%
    </span>
  );
}

function ProgressBar({ pct, color }: { pct: number; color: string }) {
  return (
    <div className="w-full h-1.5 rounded-full bg-background-200 overflow-hidden">
      <div
        className={`h-full rounded-full transition-all duration-500 ${color}`}
        style={{ width: `${Math.min(pct, 100)}%` }}
      />
    </div>
  );
}

function TotalCard({
  usage,
  features,
  history,
  barColor,
  t,
}: {
  usage: UsageInfo;
  features: PlanInfo["features"];
  history: DailySnapshot[];
  barColor: string;
  t: (key: string, options?: Record<string, unknown>) => string;
}) {
  const msgPct = features.messages > 0 ? ((usage?.messages ?? 0) / features.messages) * 100 : 0;
  const tokPct = features.tokens > 0 ? ((usage?.tokens ?? 0) / features.tokens) * 100 : 0;
  const inpPct = features.input > 0 ? ((usage?.input ?? 0) / features.input) * 100 : 0;
  const outPct = features.output > 0 ? ((usage?.output ?? 0) / features.output) * 100 : 0;
  const totalPct = Math.round((msgPct + tokPct + inpPct + outPct) / 4);
  const isOver = totalPct > 100;
  const trend = useTrend(history, "total");

  let trendText: string;
  if (trend.direction === "up") trendText = t("stats.trendingUp");
  else if (trend.direction === "down") trendText = t("stats.trendingDown");
  else trendText = t("stats.trendingFlat");

  return (
    <div className="bg-background-50 rounded-2xl border border-background-200/50 p-5 flex flex-col h-full justify-between lg:col-start-1 lg:row-start-1 lg:row-span-2">
      <div>
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-medium text-foreground-700">{t("stats.totalUsage")}</span>
          <TrendBadge pct={trend.pct} direction={trend.direction} barColor={barColor} />
        </div>
        <ProgressBar pct={totalPct} color={isOver ? "bg-red-500" : barColor} />
        <div className="mt-6">
          <p className="font-heading text-5xl text-foreground-950 mb-1">
            {totalPct}%
          </p>
          <p className="text-sm text-foreground-500">
            {t("stats.averageUsage")}
          </p>
        </div>
      </div>
      <div className="mt-6">
        <p className="text-xs text-foreground-400 mt-3 leading-relaxed">
          {trendText}. 
          {isOver ? t("stats.exceededLimit") : " "}
          {t("stats.upgradePrompt")}
        </p>
      </div>
    </div>
  );
}

function SmallCard({
  label,
  used,
  total,
  dataKey,
  history,
  barColor,
  t,
}: {
  label: string;
  used: number;
  total: number;
  dataKey: keyof DailySnapshot;
  history: DailySnapshot[];
  barColor: string;
  t: (key: string, options?: Record<string, unknown>) => string;
}) {
  const safeUsed = used ?? 0;
  const safeTotal = total ?? 0;
  const pct = safeTotal > 0 ? Math.min((safeUsed / safeTotal) * 100, 100) : 0;
  const isOver = safeUsed > safeTotal;
  const trend = useTrend(history, dataKey);

  return (
    <div className="bg-background-50 rounded-2xl border border-background-200/50 p-5 flex flex-col">
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-medium text-foreground-700">{label}</span>
        <TrendBadge pct={trend.pct} direction={trend.direction} barColor={barColor} />
      </div>
      <ProgressBar pct={pct} color={isOver ? "bg-red-500" : barColor} />
      <div className="mt-auto pt-4">
        <p className="font-heading text-3xl text-foreground-950 mb-1">
          {pct.toFixed(0)}%
        </p>
        <p className="text-xs text-foreground-500">
          {safeUsed.toLocaleString()} {t("stats.of")} {safeTotal.toLocaleString()}
        </p>
      </div>
    </div>
  );
}

export default function StatsCards({ plan, usage, history }: StatsCardsProps) {
  const { t } = useTranslation("dashboard");
  const features = plan?.features ?? { messages: 0, tokens: 0, input: 0, output: 0, total: 100 };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-heading text-lg text-foreground-950">{t("stats.usageStatistics")}</h3>
        <span className="text-xs text-foreground-400">{t("stats.last7Days")}</span>
      </div>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3 lg:grid-rows-2">
        <TotalCard
          usage={usage}
          features={features}
          history={history}
          barColor="bg-primary-500"
          t={t}
        />
        <SmallCard
          label={t("stats.messages")}
          used={usage?.messages ?? 0}
          total={features.messages}
          dataKey="messages"
          history={history}
          barColor="bg-emerald-500"
          t={t}
        />
        <SmallCard
          label={t("stats.tokens")}
          used={usage?.tokens ?? 0}
          total={features.tokens}
          dataKey="tokens"
          history={history}
          barColor="bg-secondary-500"
          t={t}
        />
        <SmallCard
          label={t("stats.inputTokens")}
          used={usage?.input ?? 0}
          total={features.input}
          dataKey="inputTokens"
          history={history}
          barColor="bg-accent-500"
          t={t}
        />
        <SmallCard
          label={t("stats.outputTokens")}
          used={usage?.output ?? 0}
          total={features.output}
          dataKey="outputTokens"
          history={history}
          barColor="bg-foreground-500"
          t={t}
        />
      </div>
    </div>
  );
}